<template>
  <el-form
    v-model="jobForm.fields"
    :model="jobForm.fields"
    label-width="150px"
    label-position="top"
    require-asterisk-position="right">
    <!-- Software -->
    <el-form-item
      v-if="softwareSelection || loadSoftwareSelection"
      :label="i18n.t('software')"
      prop="software"
      :error="jobForm.getErrorMessage('software')">
      <SmSelect
        v-model:selection="jobForm.fields.software"
        :options="softwareSelection"
        :label="i18n.t('selectSoftware')"
        :loading="loadingSoftwareSelection"
        class="w-full"
        searchable
        @blur="jobForm.validate(['sub_account'])">
        <template #item="{ item }">
          <TextMultiline>
            {{ item.value.display_name }}
            <span v-if="item.value.information.subtitle">
              ({{ item.value.information.subtitle }})</span
            >
          </TextMultiline>
        </template>
      </SmSelect>
    </el-form-item>

    <!-- License -->
    <el-form-item
      v-if="licenseSelection || loadingLicenseSelection"
      :label="i18n.t('account')"
      prop="licence"
      :error="jobForm.getErrorMessage('licence')">
      <SmSelect
        v-model:selection="jobForm.fields.licence"
        :options="minifiedLicenseOptions"
        :label="i18n.t('selectAccount')"
        :item-size="65"
        :loading="loadingLicenseSelection"
        class="w-full"
        searchable
        @blur="jobForm.validate(['licence'])">
        <template #item="{ item }">
          <div class="flex flex-col leading-6">
            <TextMultiline :text="item.value.email" />
            <TextMultiline
              class="text-contrast-muted"
              :text="
                getSoftwareLicenseName(Object.keys(item.value.softwareLicenses))
              " />
          </div>
        </template>
      </SmSelect>
    </el-form-item>

    <div class="flex gap-4">
      <!-- Date -->
      <el-form-item class="w-1/2">
        <template #label>
          <div class="flex gap-2">
            {{ i18n.t('jobExecutionInput.label') }}
            <SmTooltip>
              <template #content>
                {{ i18n.t('jobExecutionInput.tooltip') }}
              </template>
              <CircleHelp class="text-contrast-muted" :size="16" />
            </SmTooltip>
          </div>
        </template>
        <SmDatepicker
          v-model:date="jobForm.fields.enqueue_date"
          class="w-full" />
      </el-form-item>

      <el-form-item
        :label="i18n.t('time')"
        class="w-1/2"
        :error="jobForm.getErrorMessage('enqueue_in')">
        <SmTimeInput
          v-model:time="jobForm.fields.enqueue_time"
          :disabled="!jobForm.fields.enqueue_date"
          :placeholder="
            !jobForm.fields.enqueue_date
              ? i18n.t('pleaseSelectDate')
              : undefined
          "
          class="w-full" />
      </el-form-item>
    </div>
  </el-form>
</template>

<script setup lang="ts">
  import { CircleHelp } from 'lucide-vue-next'
  import {
    BackgroundJobSoftwareUserRemoveData,
    BackgroundJobType,
    Licence,
    SoftwareLicense,
    SoftwareLicenseMini_Output,
    SoftwareLicensesService,
    SoftwareOut,
  } from '@/client'
  import useForm from '@/composables/useForm'
  import { useI18n } from 'vue-i18n'
  import {
    isDateAfterNow,
    isRequired,
  } from '@/components//sm/SmInput/SmInputValidator'
  import { Option } from '@/components//sm/SmSelect.vue'
  import dayjs from 'dayjs'
  import { backgroundjobStore } from '@/stores/backgroundjob/backgroundjob.store'
  import { Request } from '@/components/tasks/TaskEditDialog.vue'
  import { AccountStore } from '@/stores/account/account.store'
  import { ApplicationStore } from '@/stores/application/application.store'
  import { ApplicationFilter } from '@/stores/application/application.filter'

  const i18n = useI18n()
  const fallBackTime = '0600'
  const tomorrowDate = dayjs().add(1, 'day').toDate()

  /**
   * Form
   */
  const jobForm = useForm(
    {
      type: 'delete_account', // the delete option in software_user_remove
      software: undefined, // for filtering licenses
      licence: undefined,
      // disable: false,
      // delete: true,
      enqueue_in: tomorrowDate as Date | null,
      enqueue_date: tomorrowDate,
      enqueue_time: fallBackTime,
    },
    // Form Hooks
    {
      onReset: () => {},
    },

    // Form Validators
    {
      software: [isRequired],
      licence: [isRequired],
      enqueue_in: [isDateAfterNow(600, true)],
    },
    {}
  )

  const selectedDateTime = computed(() => {
    if (!jobForm.fields.enqueue_date) return null

    const date = dayjs(jobForm.fields.enqueue_date)
    const time = jobForm.fields.enqueue_time || fallBackTime

    const dateTime = date
      .hour(parseInt(time.slice(0, 2)))
      .minute(parseInt(time.slice(2, 4)))

    return dateTime.toDate()
  })

  watch(
    selectedDateTime,
    (val) => {
      jobForm.fields.enqueue_in = val
    },
    { immediate: true }
  )

  type simple_license = {
    _id: string
    email: string
    software_id: string
    softwareLicenses: Record<string, SoftwareLicenseMini_Output>
  }

  const minifiedLicenseOptions = computed<Option<simple_license>[] | null>(
    () => {
      if (loadingLicenseSelection.value) return null
      if (!licenseSelection.value) return []

      return licenseSelection.value.map((option) => ({
        value: {
          _id: option.value._id,
          email: option.value.email,
          software_id: option.value.software_id,
          softwareLicenses: option.value.software_licenses,
        },
        label: option.label,
        key: option.key,
      }))
    }
  )

  function createRequestBody(): Request<BackgroundJobSoftwareUserRemoveData> {
    const formFields = JSON.parse(JSON.stringify(toRaw(jobForm).fields))
    const requestBody: Request<BackgroundJobSoftwareUserRemoveData> = {
      type: BackgroundJobType.SOFTWARE_USER_REMOVE,
      data: {} as BackgroundJobSoftwareUserRemoveData,
      enqueue_in: 0,
    }

    // extract ids from objects
    if (formFields.licence && typeof formFields.licence == 'object') {
      requestBody.data.licence_id = formFields.licence[0].value._id
    }

    requestBody.data.delete = true
    requestBody.data.disable = false

    // Convert dateTime to seconds
    if (selectedDateTime.value) {
      requestBody.enqueue_in = dayjs(selectedDateTime.value).diff(dayjs(), 's')
    }

    return requestBody
  }

  function submit() {
    if (!jobForm?.dirty) if (!jobForm.validate()) return

    const requestBody = createRequestBody()
    const { type, enqueue_in, data } = requestBody

    return backgroundjobStore
      .createBackgroundjob(type, data, enqueue_in)
      .then((response) => {
        return response
      })
  }

  function resetForm() {
    jobForm?.reset()
  }

  function validateForm() {
    return jobForm?.validate()
  }
  /**
   * Selection Data
   */

  const licenseSelection = ref<Option<Licence>[] | undefined>([])
  const loadingLicenseSelection = ref<boolean>(false)
  const softwareSelection = ref<Option<SoftwareOut>[] | undefined>([])
  const loadingSoftwareSelection = ref<boolean>(false)

  async function loadSoftwareSelection() {
    loadingSoftwareSelection.value = true
    const filters: ApplicationFilter = { canDeleteUser: true }

    const availableApplications =
      await ApplicationStore.getApplications<SoftwareOut>(filters)

    softwareSelection.value = availableApplications.map((software) => ({
      value: software,
      label: software.display_name,
      key: software._id,
    }))
    loadingSoftwareSelection.value = false
  }

  async function loadLicenceSelection(software: Option<SoftwareOut>) {
    loadingLicenseSelection.value = true

    const allAccounts = await AccountStore.getAccounts({
      applicationId: software.value._id,
    })

    licenseSelection.value = allAccounts?.map((software) => ({
      value: toRaw(software),
      label: software.email,
      key: software._id,
    }))
    loadingLicenseSelection.value = false
  }

  function getSoftwareLicenseName(licenses: string[]) {
    const length = Object.keys(licenses).length

    if (length == 0) {
      return
    } else if (length == 1) {
      const license = softwareLicenses.value.find(
        (license) => license._id === licenses[0]
      )
      return license?.name
    } else {
      return i18n.t('licensesCount', { count: length })
    }
  }

  const softwareLicenses = ref<SoftwareLicense[]>([])
  async function loadSoftwareLicenses(application_id: string) {
    softwareLicenses.value =
      await SoftwareLicensesService.getSoftwareLicensesForSoftwareApiV1SoftwareSoftwareSoftwareIdLicensesGet(
        { softwareId: application_id }
      )
  }

  watch(
    () => jobForm.fields.software,
    (
      newValue: Option<SoftwareOut>[] | undefined,
      oldValue: Option<SoftwareOut>[] | undefined
    ) => {
      const deselected = !newValue?.length
      const changedSelected =
        newValue?.[0] && oldValue?.[0] && newValue[0] !== oldValue[0]
      const selected = newValue?.length && !oldValue?.length

      if (deselected || changedSelected) {
        licenseSelection.value = jobForm.fields.licence = undefined
      }
      if (changedSelected || selected) {
        loadLicenceSelection(newValue[0] as Option<SoftwareOut>)
        loadSoftwareLicenses(newValue[0].value._id)
      }

      // setCheckboxSettings(newValue)
    },
    { immediate: true, deep: true }
  )

  // function setCheckboxSettings(newValue: Option<SoftwareOut>[] | undefined) {
  //   if (!newValue) {
  //     canDelete.value = false
  //     canDisable.value = false
  //     checkboxVisibility.value = false

  //     return
  //   }

  //   // check disable
  //   if (newValue[0].value.information.implements_disable_licence_method) {
  //     canDisable.value = true
  //   } else {
  //     canDisable.value = false
  //   }

  //   // check delete
  //   if (newValue?.[0].value.information.implements_delete_user_method) {
  //     canDelete.value = true
  //   } else {
  //     canDelete.value = false
  //   }

  //   // set message & form
  //   if (canDelete.value && !canDisable.value) {
  //     // case: only delete
  //     jobForm.fields.delete = true
  //     blockDeleteCheckbox.value = true
  //     checkboxVisibility.value = true
  //     checkboxMessage.value = i18n.t('canOnlyDeleteUser')
  //   } else if (!canDelete.value && canDisable.value) {
  //     // case: only disable
  //     jobForm.fields.disable = true
  //     blockDisableCheckbox.value = true
  //     checkboxVisibility.value = true
  //     checkboxMessage.value = i18n.t('canOnlyDisableUser')
  //   } else {
  //     checkboxVisibility.value = false
  //   }
  // }

  onMounted(async () => {
    loadSoftwareSelection()
  })

  defineExpose({ submit, resetForm, validateForm, createRequestBody })
</script>

<style scoped></style>
